import { converterNumeroBR } from '../lib/textoBR'
import { ICMS_INTERNO_POR_UF } from '../impostos/icmsInternoPorUf'
import type { LinhaMapeada, ResultadoValidacao } from './validacao'

// Mesma lista canonica de UFs usada pelo motor de imposto (icmsInternoPorUf.ts)
// -- as 27 chaves desse mapa SAO as UFs que o app reconhece. Reusar em vez de
// duplicar evita as duas listas discordarem se um estado mudar de sigla.
const UFS_VALIDAS = new Set(Object.keys(ICMS_INTERNO_POR_UF))

// Mesmos tres valores da constraint de `sub_pedidos.frete_tipo` e
// `descontos_frete.incoterm` na migration 0024.
const INCOTERMS_VALIDOS = new Set(['CIF', 'FOB', 'REDESPACHO'])

export type TipoDeAliquota = 'icms' | 'st' | 'desconto_frete'

// Qual das 3 abas da planilha canonica esta linha representa. As tres abas
// NUNCA compartilham a coluna de VALOR entre si (aliquota_pct so existe na
// aba ICMS, mva_pct so na ST, desconto_pct so na DESCONTO_FRETE) -- entao a
// coluna que a pessoa mapeou, sozinha, ja basta para saber qual forma vale
// para o resto da linha. Mesma leitura aqui e no envio a edge function
// (ImportarArquivo.tsx), para as duas pontas nunca discordarem sobre o que
// esta linha e.
export function detectarTipoDeAliquota(campos: Record<string, string>): TipoDeAliquota | null {
  if ('aliquota_pct' in campos) return 'icms'
  if ('mva_pct' in campos) return 'st'
  if ('desconto_pct' in campos) return 'desconto_frete'
  return null
}

// Percentual OBRIGATORIO -- ao contrario de converterPercentualTributo
// (validacao.ts), que existe para campos fiscais OPCIONAIS do produto. Aqui a
// celula vazia nao e "ninguem tem opiniao": e a razao da linha existir, entao
// devolve null tanto para vazio quanto para sujo -- quem chama decide a
// mensagem (ausente vs invalido) olhando o texto original.
//
// `max` e parametro, e nao fixo em 100, porque o MVA de ST legitimamente
// passa de 100% (a migration 0024 aceita ate 200) enquanto ICMS e desconto de
// frete param em 100.
function converterPercentualObrigatorio(texto: string, max: number): number | null {
  const limpo = texto.replace(/%/g, '').trim()
  if (limpo === '') return null
  const n = converterNumeroBR(limpo)
  if (n === null || n < 0 || n > max) return null
  return n
}

export function converterAliquotaIcms(texto: string): number | null {
  return converterPercentualObrigatorio(texto, 100)
}
export function converterMvaPct(texto: string): number | null {
  return converterPercentualObrigatorio(texto, 200)
}
export function converterDescontoFretePct(texto: string): number | null {
  return converterPercentualObrigatorio(texto, 100)
}

export function normalizarUf(texto: string): string {
  return texto.trim().toUpperCase()
}

export function normalizarIncoterm(texto: string): string {
  return texto.trim().toUpperCase()
}

// Mesmo formato de erro que validarProdutos/validarClientes (validacao.ts):
// linha, tipo, motivo e o valor ofensivo quando ha um. Devolve o MESMO
// ResultadoValidacao daquele modulo -- por isso os tipos novos entram no
// union TipoErroLinha de validacao.ts, e a previa/agruparErros continuam
// funcionando sem saber que esta e uma planilha fiscal.
export function validarAliquotas(linhas: LinhaMapeada[]): ResultadoValidacao {
  const validas: LinhaMapeada[] = []
  const erros: ResultadoValidacao['erros'] = []
  for (const linha of linhas) {
    const tipoLinha = detectarTipoDeAliquota(linha.campos)

    if (tipoLinha === 'icms' || tipoLinha === 'st') {
      const codigoProduto = linha.campos.codigo_produto ?? ''
      const ufTexto = linha.campos.uf_destino ?? ''
      const uf = normalizarUf(ufTexto)
      const valorTexto = tipoLinha === 'icms'
        ? (linha.campos.aliquota_pct ?? '')
        : (linha.campos.mva_pct ?? '')
      const valor = tipoLinha === 'icms'
        ? converterAliquotaIcms(valorTexto)
        : converterMvaPct(valorTexto)

      if (!codigoProduto) {
        erros.push({ linha: linha.indice, tipo: 'codigo_produto_vazio', motivo: 'código do produto vazio' })
      } else if (!ufTexto) {
        erros.push({ linha: linha.indice, tipo: 'uf_destino_vazia', motivo: 'UF de destino vazia' })
      } else if (!UFS_VALIDAS.has(uf)) {
        erros.push({
          linha: linha.indice, tipo: 'uf_destino_invalida', valor: ufTexto,
          motivo: `UF de destino inválida: "${ufTexto}"`,
        })
      } else if (valorTexto.trim() === '') {
        erros.push({
          linha: linha.indice,
          tipo: tipoLinha === 'icms' ? 'aliquota_pct_ausente' : 'mva_pct_ausente',
          motivo: tipoLinha === 'icms' ? 'alíquota ICMS % ausente' : 'MVA % ausente',
        })
      } else if (valor === null) {
        erros.push({
          linha: linha.indice,
          tipo: tipoLinha === 'icms' ? 'aliquota_pct_invalida' : 'mva_pct_invalido',
          valor: valorTexto,
          motivo: tipoLinha === 'icms'
            ? `alíquota ICMS % inválida: "${valorTexto}"`
            : `MVA % inválido: "${valorTexto}"`,
        })
      } else {
        validas.push(linha)
      }
      continue
    }

    if (tipoLinha === 'desconto_frete') {
      const regiao = linha.campos.regiao_destino ?? ''
      const incotermTexto = linha.campos.incoterm ?? ''
      const incoterm = normalizarIncoterm(incotermTexto)
      const descontoTexto = linha.campos.desconto_pct ?? ''
      const desconto = converterDescontoFretePct(descontoTexto)

      if (!regiao) {
        erros.push({ linha: linha.indice, tipo: 'regiao_destino_vazia', motivo: 'região de destino vazia' })
      } else if (!incotermTexto) {
        erros.push({ linha: linha.indice, tipo: 'incoterm_vazio', motivo: 'incoterm vazio' })
      } else if (!INCOTERMS_VALIDOS.has(incoterm)) {
        erros.push({
          linha: linha.indice, tipo: 'incoterm_invalido', valor: incotermTexto,
          motivo: `incoterm inválido: "${incotermTexto}" (use CIF, FOB ou REDESPACHO)`,
        })
      } else if (descontoTexto.trim() === '') {
        erros.push({ linha: linha.indice, tipo: 'desconto_pct_ausente', motivo: 'desconto de frete % ausente' })
      } else if (desconto === null) {
        erros.push({
          linha: linha.indice, tipo: 'desconto_pct_invalido', valor: descontoTexto,
          motivo: `desconto de frete % inválido: "${descontoTexto}"`,
        })
      } else {
        validas.push(linha)
      }
      continue
    }

    // Nenhuma das tres colunas de valor foi mapeada -- a pessoa nao mapeou
    // Alíquota ICMS %, MVA % nem Desconto %, entao nao ha o que gravar.
    erros.push({
      linha: linha.indice, tipo: 'tipo_de_linha_desconhecido',
      motivo: 'nenhuma coluna de alíquota ICMS, MVA ou desconto de frete foi mapeada',
    })
  }
  return { validas, erros }
}

// --- payload para a edge function `aplicar-importacao-fiscal` ---
//
// So chamadas sobre linhas que JA passaram por validarAliquotas (isto e,
// `resultado.validas`): o `throw` abaixo e defensivo, nunca deveria disparar
// de verdade, e existe para falhar ALTO (e nao gravar lixo calado) se algum
// dia esta premissa for quebrada por engano.
export function paraLinhaIcms(campos: Record<string, string>): {
  codigo_produto: string; uf_destino: string; aliquota_pct: number
} {
  const aliquota = converterAliquotaIcms(campos.aliquota_pct ?? '')
  if (aliquota === null) throw new Error('Linha de ICMS sem alíquota válida (deveria ter sido barrada antes).')
  return {
    codigo_produto: campos.codigo_produto,
    uf_destino: normalizarUf(campos.uf_destino ?? ''),
    aliquota_pct: aliquota,
  }
}

export function paraLinhaSt(campos: Record<string, string>): {
  codigo_produto: string; uf_destino: string; mva_pct: number
} {
  const mva = converterMvaPct(campos.mva_pct ?? '')
  if (mva === null) throw new Error('Linha de ST sem MVA válido (deveria ter sido barrada antes).')
  return {
    codigo_produto: campos.codigo_produto,
    uf_destino: normalizarUf(campos.uf_destino ?? ''),
    mva_pct: mva,
  }
}

export function paraLinhaDescontoFrete(campos: Record<string, string>): {
  regiao_destino: string; incoterm: 'CIF' | 'FOB' | 'REDESPACHO'; desconto_pct: number
} {
  const desconto = converterDescontoFretePct(campos.desconto_pct ?? '')
  if (desconto === null) throw new Error('Linha de desconto de frete sem percentual válido (deveria ter sido barrada antes).')
  const incoterm = normalizarIncoterm(campos.incoterm ?? '')
  if (incoterm !== 'CIF' && incoterm !== 'FOB' && incoterm !== 'REDESPACHO') {
    throw new Error('Linha de desconto de frete com incoterm inválido (deveria ter sido barrada antes).')
  }
  return { regiao_destino: campos.regiao_destino, incoterm, desconto_pct: desconto }
}
